import React, { useEffect, useState } from "react";

// Optional hero image (replace with your real asset or remove the <img>)
import heroImg from "./assets/museumQr.webp";

const WHATSAPP_PHONE = "254700000000"; // update (no +)
const whatsappUrl = `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(
    "Hi! I'm interested in Dynamic QR codes for Museums & Galleries."
)}`;

export default function MuseumsGalleriesPage() {
    // Smooth scroll (no URL change)
    const NAV_H = 72;
    function scrollToId(id) {
        const el = document.getElementById(id);
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.pageYOffset - NAV_H - 8;
        window.scrollTo({ top, behavior: "smooth" });
    }
    function onNavClick(id) {
        scrollToId(id);
        const toggle = document.getElementById("museum-nav-toggle");
        if (toggle && toggle.checked) toggle.checked = false;
    }

    return (
        <div className="museums-page">

            {/* HERO */}
            <section id="home" className="hero">
                <div className="container hero-grid">

                    <div className="hero-copy">
                        <h1>
                            Dynamic QR Codes <span className="accent">for Museums & Galleries</span>
                        </h1>
                        <p className="lead">
                            One code, many stories — artwork labels, audio guides, artist bios, AR links, donations, and more.
                            Update content anytime without reprinting.
                        </p>

                        <div className="row gap hero-actions">
                            <button type="button" className="btn primary" onClick={() => onNavClick("multicontent")}>
                                See Multi-Content QR
                            </button>
                            <button type="button" className="btn ghost" onClick={() => onNavClick("features")}>
                                Explore Features
                            </button>
                            <a href={whatsappUrl} className="btn whats" target="_blank" rel="noreferrer">
                                Talk on WhatsApp
                            </a>
                        </div>

                        <ul className="hero-bullets">
                            <li>Switch languages and content by visitor device or location</li>
                            <li>Schedule content for rotating exhibitions</li>
                            <li>Collect feedback and donations directly</li>
                        </ul>
                    </div>


                    {/* Image first on mobile (handled in CSS with order) */}
                    <div className="hero-art">
                        <img
                            src={heroImg}
                            alt="Museum gallery with QR labels next to artworks"
                            className="hero-img"
                            loading="eager"
                            decoding="async"
                        />
                    </div>
                </div>
            </section>

            {/* USE CASES */}
            <section id="usecases" className="section">
                <div className="container">
                    <h2>Where One QR Shines</h2>
                    <p className="muted max">Your wall label becomes a doorway: multiple content views from the same code.</p>

                    <div className="grid usecases">
                        <UseCase emoji="🖼️" title="Artwork Labels">
                            Show piece title, year, medium, and extended curatorial notes with references.
                        </UseCase>
                        <UseCase emoji="🎧" title="Audio Guides">
                            Offer short tracks or full tours. Auto-play per artwork if allowed by the browser.
                        </UseCase>
                        <UseCase emoji="🌐" title="Language Switching">
                            Detect device language and default to it; let visitors switch manually.
                        </UseCase>
                        <UseCase emoji="🕶️" title="AR / 3D Links">
                            Hand off to AR viewers or 3D models with safe deep links.
                        </UseCase>
                        <UseCase emoji="💬" title="Visitor Feedback">
                            Capture quick reactions or longer comments; export CSV.
                        </UseCase>
                        <UseCase emoji="❤️" title="Donations">
                            Route visitors to donation or membership pages; add UTM for attribution.
                        </UseCase>
                    </div>
                </div>
            </section>

            {/* MULTI-CONTENT QR DEMO */}
            <section id="multicontent" className="section muted-bg">
                <div className="container">
                    <h2>One QR → Many Views</h2>
                    <p className="muted max">
                        A single QR can present multiple content types. Visitors tap tabs to switch —
                        or you can default/tailor the view by language, time, or location.
                    </p>

                    <div className="grid multi">
                        <MultiContentCard />
                        <BenefitsList />
                    </div>

                    <div className="cta-slab">
                        <p className="muted">Need to add a new tab for an upcoming curator’s talk? No reprint required.</p>
                        <a href={whatsappUrl} className="btn primary" target="_blank" rel="noreferrer">Start a Pilot</a>
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            <section id="features" className="section">
                <div className="container">
                    <h2>Built for Exhibitions</h2>
                    <div className="grid features">
                        <Feature emoji="🔁" title="Dynamic Destinations">
                            Update, append, or schedule content per artwork or gallery space.
                        </Feature>
                        <Feature emoji="🌍" title="Language & Geo Routing">
                            Auto-select by device language or location; visitors can switch anytime.
                        </Feature>
                        <Feature emoji="🧪" title="A/B Testing">
                            Test short vs. long labels or audio lengths and see engagement.
                        </Feature>
                        <Feature emoji="📈" title="Scan Analytics">
                            Track scans by hour, device, and approximate location; export CSV.
                        </Feature>
                        <Feature emoji="🏷️" title="Auto UTM Tagging">
                            Attribute donations, memberships, and shop conversions.
                        </Feature>
                        <Feature emoji="🔒" title="Anti-Abuse">
                            Rate-limit bursts, optional cool-downs, and anomaly flags.
                        </Feature>
                    </div>
                </div>
            </section>

            {/* PRICING */}
            <section id="pricing" className="section">
                <div className="container">
                    <h2>Pricing</h2>
                    <p className="muted max">Simple tiers for small galleries to national museums.</p>

                    <div className="grid pricing">
                        <div className="price">
                            <h3>Gallery</h3>
                            <p className="price-num">KES 4,900 <span>/month</span></p>
                            <ul>
                                <li>10 artwork QRs</li>
                                <li>5k scans / month</li>
                                <li>Multi-content tabs + basic analytics</li>
                            </ul>
                            <a href={whatsappUrl} className="btn block whats" target="_blank" rel="noreferrer">Choose Gallery</a>
                        </div>

                        <div className="price popular" aria-label="Most popular">
                            <div className="badge">Most Popular</div>
                            <h3>Museum</h3>
                            <p className="price-num">KES 14,900 <span>/month</span></p>
                            <ul>
                                <li>50 artwork QRs</li>
                                <li>30k scans / month</li>
                                <li>Language routing + CSV export</li>
                            </ul>
                            <a href={whatsappUrl} className="btn primary block" target="_blank" rel="noreferrer">Choose Museum</a>
                        </div>

                        <div className="price">
                            <h3>Enterprise</h3>
                            <p className="price-num">Custom</p>
                            <ul>
                                <li>Unlimited codes & scans</li>
                                <li>Private API + SSO</li>
                                <li>Priority support</li>
                            </ul>
                            <a href={whatsappUrl} className="btn block" target="_blank" rel="noreferrer">Contact Sales</a>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="section">
                <div className="container">
                    <h2>FAQ</h2>
                    <div className="faq">
                        <details>
                            <summary>Can one QR hold multiple pieces of information?</summary>
                            <p>Yes. We render multiple tabs (Label, Audio, Bio, AR, Feedback, Donate). You can add/remove tabs anytime.</p>
                        </details>
                        <details>
                            <summary>Can I set different content per language?</summary>
                            <p>Yes. Auto-detect device language and let visitors switch manually.</p>
                        </details>
                        <details>
                            <summary>Do I need to reprint if the exhibition changes?</summary>
                            <p>No. Update the QR content in the dashboard; the printed code stays the same.</p>
                        </details>
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="footer">
                <div className="container footer-row">
                    <span className="muted tiny">© {new Date().getFullYear()} QR Flex</span>
                    <button type="button" className="tiny linklike" onClick={() => onNavClick("home")}>
                        Back to top ↑
                    </button>
                </div>
            </footer>
        </div>
    );
}

/* ========== Small building blocks ========== */

function UseCase({ emoji, title, children }) {
    return (
        <div className="usecase">
            <div className="usecase-head">
                <span className="emoji" aria-hidden="true">{emoji}</span>
                <h3>{title}</h3>
            </div>
            <p className="muted">{children}</p>
        </div>
    );
}

function Feature({ emoji, title, children }) {
    return (
        <div className="feature">
            <div className="feature-head">
                <span className="emoji" aria-hidden="true">{emoji}</span>
                <h3>{title}</h3>
            </div>
            <p className="muted">{children}</p>
        </div>
    );
}

/** Demo card showing multiple content views for one QR */
function MultiContentCard() {
    const tabs = [
        { key: "label", name: "Label", emoji: "🖼️" },
        { key: "audio", name: "Audio", emoji: "🎧" },
        { key: "bio", name: "Artist Bio", emoji: "🧑‍🎨" },
        { key: "ar", name: "AR / 3D", emoji: "🕶️" },
        { key: "form", name: "Feedback", emoji: "💬" },
        { key: "donate", name: "Donate", emoji: "❤️" },
    ];
    const [active, setActive] = useState("label");

    return (
        <div className="multicard" role="group" aria-label="Multi-content QR demo">
            <div className="multicard-head">
                <span className="qr-emoji" aria-hidden="true">🔳</span>
                <div className="pillbar" role="tablist" aria-label="Content tabs">
                    {tabs.map(t => (
                        <button
                            key={t.key}
                            role="tab"
                            aria-selected={active === t.key}
                            className={`pill ${active === t.key ? "active" : ""}`}
                            onClick={() => setActive(t.key)}
                        >
                            <span className="emoji" aria-hidden="true">{t.emoji}</span> {t.name}
                        </button>
                    ))}
                </div>
            </div>

            <div className="multicard-body">
                {active === "label" && (
                    <div className="pane">
                        <h3>Artwork Label</h3>
                        <p><strong>“Sunlit Courtyard”</strong> (1927) — Oil on canvas, 64 × 48 cm.</p>
                        <p className="muted">A study in light and negative space drawn from coastal East Africa.</p>
                    </div>
                )}
                {active === "audio" && (
                    <div className="pane">
                        <h3>Audio Guide (1:12)</h3>
                        <p className="muted">Press play to hear the curator discuss the brush technique and influences.</p>
                        <div className="audio-faux">▶︎ Play (demo)</div>
                    </div>
                )}
                {active === "bio" && (
                    <div className="pane">
                        <h3>Artist Bio</h3>
                        <p><strong>A. Nyaga</strong> (b. 1899) blended impressionism with Swahili coastal palettes.</p>
                        <p className="muted">Includes references and further reading links.</p>
                    </div>
                )}
                {active === "ar" && (
                    <div className="pane">
                        <h3>AR / 3D</h3>
                        <p>Open a 3D model or place the piece in your room with AR (device-compatible links).</p>
                        <button className="btn ghost">Open AR</button>
                    </div>
                )}
                {active === "form" && (
                    <div className="pane">
                        <h3>Visitor Feedback</h3>
                        <p className="muted">Quick reaction form; export responses as CSV.</p>
                        <button className="btn">Leave Feedback</button>
                    </div>
                )}
                {active === "donate" && (
                    <div className="pane">
                        <h3>Donate</h3>
                        <p className="muted">Support conservation, research, and educational programs.</p>
                        <button className="btn primary">Donate</button>
                    </div>
                )}
            </div>
        </div>
    );
}

function BenefitsList() {
    return (
        <div className="benefits">
            <h3>Why multi-content matters</h3>
            <ul className="list">
                <li><strong>One label, many stories:</strong> no clutter on walls.</li>
                <li><strong>Flexible & future-proof:</strong> change content without reprinting.</li>
                <li><strong>Inclusive:</strong> language switching and audio accessibility.</li>
                <li><strong>Measurable:</strong> see what visitors engage with most.</li>
            </ul>
            <div className="row gap wrap">
                <a href={whatsappUrl} className="btn primary" target="_blank" rel="noreferrer">Book a Walkthrough</a>
                <button className="btn ghost" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
                    Preview on Mobile
                </button>
            </div>
        </div>
    );
}
